import { useDisclosure } from '@mantine/hooks';
import { useIntersectionObserver } from '@siberiacancode/reactuse';
import React from 'react';
import { generatePath, NavLink, useParams } from 'react-router-dom';

import { rootRoutes } from '../../../../app/routers/rootRoutes';
import { useAppSelector } from '../../../../app/stores/hooks';
import { selectSort, TaskGroups, useGetTaskListQuery } from '../../../../entities/tasks';
import { selectFilteredTasks } from '../../../../entities/tasks/model/selectors/task-list';
import { ReactComponent as Hamburger } from '../../../../shared/assets/hamburger.svg';
import { ReactComponent as Search } from '../../../../shared/assets/search.svg';
import { allTasksLink, rootSettings, texts } from '../../../../shared/config';
import { sortTaskFunc } from '../../../../shared/lib';
import { TaskInList } from '../../../../shared/model/tasks/types';
import { ContentLayout } from '../../../../shared/ui/ContentLayout';
import { Header } from '../../../../shared/ui/Header';
import { LinearProgress } from '../../../../shared/ui/LinearProgress';
import { Message } from '../../../../shared/ui/Message';
import { TaskList } from '../../../../shared/ui/TaskList';
import { TaskMenu } from '../../../../shared/ui/TaskMenu';
import { TaskNavigationLink } from '../../../../shared/ui/TaskNavigationLink';

export const AllTaskList: React.FC = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const { groupId, filterId, ...params } = useParams();
  const sort = useAppSelector(selectSort);
  const [offset, setOffset] = React.useState<number>(0);
  const groupKey = params.groupKey ? decodeURI(params.groupKey) : TaskGroups.None;

  const settings = React.useMemo(() => ({ groupId, sort }), [groupId, sort]);

  const { filteredData, isError, isFetching, isSuccess } = useGetTaskListQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      filteredData: selectFilteredTasks(result, settings),
    }),
  });

  const { countsMap, taskListsMap } = filteredData;

  const taskList = React.useMemo(() => {
    let tasks: TaskInList[] | undefined = taskListsMap[groupKey];
    if (filterId === 'new') {
      tasks = tasks?.filter((v) => !v.viewed);
    }

    if (filterId === 'viewed') {
      tasks = tasks?.filter((v) => v.viewed);
    }

    return tasks?.sort(sortTaskFunc(...sort)) ?? [];
  }, [filterId, taskListsMap]);

  const taskListSlice = React.useMemo(() => {
    return taskList.slice(0, offset);
  }, [offset, taskList]);

  const getTaskNavLinkPath = (filterId = '', path = '') => {
    return groupKey === TaskGroups.None
      ? `${rootRoutes.taskList}${path}`
      : generatePath(rootRoutes.filteredTaskList, {
          groupKey,
          groupId,
          filterId,
        });
  };

  const { ref } = useIntersectionObserver<HTMLDivElement>({
    threshold: 1,
    onChange: (entry) => {
      if (entry.isIntersecting) {
        setOffset((prev) => prev + rootSettings.perPage);
      }
    },
  });

  const getHeaderProps = () => {
    return {
      leftIcon: groupKey === TaskGroups.None ? <Hamburger data-id="hamburgerIcon" onClick={open} /> : undefined,
      goBackLink: groupKey !== TaskGroups.None ? generatePath(rootRoutes.groupedTaskList, { groupId }) : undefined,
    };
  };

  return (
    <>
      <Header
        {...getHeaderProps()}
        rightIcon={
          <NavLink to={rootRoutes.search} end>
            <Search data-id="searchIcon" />
          </NavLink>
        }
      >
        Задачи
      </Header>

      {isFetching && <LinearProgress position="absolute" />}

      <ContentLayout dataId="allTaskList">
        {isSuccess && (
          <>
            <nav data-id="taskNavigation">
              {allTasksLink.map(({ filter, header, id, path }) => {
                const count = countsMap[groupKey];

                if (filter === filterId || !count || count[id] < 1) {
                  return null;
                }

                return (
                  <TaskNavigationLink count={count[id]} key={id} name={header} to={getTaskNavLinkPath(filter, path)} />
                );
              })}
            </nav>
            {taskList.length ? (
              <TaskList values={taskListSlice} />
            ) : (
              <Message title="Нет задач, соответствующих запросу" />
            )}
          </>
        )}
        <div ref={ref} />
        {isError && <Message title={texts.defaultError} />}
      </ContentLayout>
      <TaskMenu opened={opened} onClose={close} />
    </>
  );
};
